import { PlanContext } from './context.types';
import { ScenarioCriteria, ScenarioResponse } from './scenario.model';

/**
 * Options for AI provider completion requests
 */
export interface AIProviderOptions {
  /** Maximum number of tokens to generate */
  maxTokens?: number;
  /** Temperature for response randomness (0-2) */
  temperature?: number;
  /** Response format preference */
  responseFormat?: 'text' | 'json_object';
  /** Model version to use (overrides default) */
  modelVersion?: string;
}

/**
 * Interface contract for all AI providers
 * Implementations can be Azure OpenAI, OpenAI, local models, or rule-based fallback
 */
export interface IAIProvider {
  /**
   * Unique identifier for this provider
   * @returns Provider name (e.g., 'azure-openai', 'fallback')
   */
  getName(): string;

  /**
   * Check if provider is properly configured and available
   * @returns true if provider can accept requests
   */
  isAvailable(): boolean;

  /**
   * Generate a text completion for given prompts
   * @param systemPrompt - System context/instructions
   * @param userPrompt - User's input/question
   * @param options - Optional generation parameters
   * @returns Generated text response
   * @throws Error if provider is unavailable or request fails
   */
  generateCompletion(systemPrompt: string, userPrompt: string, options?: AIProviderOptions): Promise<string>;

  /**
   * Generate workforce planning scenarios
   * @param planContext - Full context of the plan including employees and shifts
   * @param criteria - Optimization criteria for scenario generation
   * @returns Generated scenarios with assignments and metrics
   * @throws Error if generation fails
   */
  generateScenarios(planContext: PlanContext, criteria: ScenarioCriteria): Promise<ScenarioResponse>;
}

/**
 * AI Provider injection token for NestJS DI
 */
export const AI_PROVIDER = Symbol('AI_PROVIDER');

/**
 * Information about active and available AI providers
 */
export interface ProviderInfo {
  activeProvider: string;
  availableProviders: string[];
}
